import { DateTime } from "luxon";
import type { Mentorship } from "@/lib/api/mentorships";

export type MentorshipBucket = "active" | "paused" | "past";

export function mentorshipBucket(mentorship: Mentorship): MentorshipBucket {
  if (!mentorship.deactivated) return "active";
  if (mentorship.rematched_to_id) return "past";
  if (mentorship.latest_deactivation_kind === "pause") return "paused";
  return "past";
}

function startedAtMillis(startDate: string): number {
  const started = DateTime.fromISO(startDate);
  return started.isValid ? started.toMillis() : 0;
}

export function reactivateMentorshipDisabled(params: {
  isMentor: boolean;
  hasActiveMentorship: boolean;
  menteeDeactivated: boolean;
  mentorDeactivated: boolean;
}): boolean {
  if (params.menteeDeactivated || params.mentorDeactivated) return true;
  if (!params.isMentor && params.hasActiveMentorship) return true;
  return false;
}

export type MentorshipSortColumn = "name" | "days";
export type MentorshipSortDirection = "asc" | "desc";

export type ActiveMentorshipSort = {
  column: MentorshipSortColumn;
  direction: MentorshipSortDirection;
};

export function sortActiveMentorships(
  rows: Mentorship[],
  sort: ActiveMentorshipSort | null,
  isMentor: boolean,
): Mentorship[] {
  if (!sort) return rows;

  const direction = sort.direction === "asc" ? 1 : -1;

  return [...rows].sort((a, b) => {
    if (sort.column === "name") {
      const aName = isMentor ? a.mentee_name : a.mentor_name;
      const bName = isMentor ? b.mentee_name : b.mentor_name;
      return (
        aName.localeCompare(bName, undefined, { sensitivity: "base" }) *
        direction
      );
    }

    const aDays = Number(a.days_in_mentorship);
    const bDays = Number(b.days_in_mentorship);
    const aValue = Number.isFinite(aDays) ? aDays : -1;
    const bValue = Number.isFinite(bDays) ? bDays : -1;
    return (aValue - bValue) * direction;
  });
}

export function groupMentorshipRows(mentorships: Mentorship[]): {
  active: Mentorship[];
  paused: Mentorship[];
  past: Mentorship[];
} {
  const grouped: Record<MentorshipBucket, Mentorship[]> = {
    active: [],
    paused: [],
    past: [],
  };

  for (const mentorship of mentorships) {
    grouped[mentorshipBucket(mentorship)].push(mentorship);
  }

  const newestFirst = (rows: Mentorship[]) =>
    [...rows].sort(
      (a, b) => startedAtMillis(b.start_date) - startedAtMillis(a.start_date),
    );

  return {
    active: newestFirst(grouped.active),
    paused: newestFirst(grouped.paused),
    past: newestFirst(grouped.past),
  };
}
